import { CameraOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 撮影した写真を File として返す。呼び出し側でアップロード処理を行う。 */
  onCapture: (file: File) => void;
  disabled?: boolean;
};

/**
 * Webカメラでの撮影モーダル。
 *
 * getUserMedia でカメラ映像をプレビューし、シャッターボタンで
 * canvas にキャプチャ → JPEG Blob → File に変換して返す。
 * モーダルを閉じた際・unmount 時にストリームを確実に stop() する。
 */
export default function CameraCapture({
  open,
  onClose,
  onCapture,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [captured, setCaptured] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState(false);

  /** カメラストリームを開始する */
  const startCamera = useCallback(async () => {
    setCaptured(null);
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      setCameraError(true);
    }
  }, []);

  /** カメラストリームを停止する */
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // モーダルが開いたらカメラ起動、閉じたら停止
  useEffect(() => {
    if (open) {
      void startCamera();
    } else {
      stopCamera();
      setCaptured(null);
    }
    return () => {
      stopCamera();
    };
  }, [open, startCamera, stopCamera]);

  /** シャッター: video の現フレームを canvas に描画して JPEG 化 */
  const handleShutter = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setCaptured(dataUrl);
    stopCamera();
  };

  /** 「この写真を使う」: dataURL → Blob → File に変換して親に返す */
  const handleUse = () => {
    if (!captured) return;
    const byteString = atob(captured.split(',')[1]);
    const mimeString = captured.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], `capture_${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });
    onCapture(file);
    onClose();
  };

  /** 「撮り直す」: プレビューを消してカメラを再起動 */
  const handleRetake = () => {
    setCaptured(null);
    void startCamera();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={t('photo.capture')}
      destroyOnHidden
      width="95%"
      style={{ maxWidth: 1200, top: 20 }}
      styles={{ body: { padding: 0 } }}
    >
      {/* 非表示 canvas（キャプチャ用） */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {cameraError ? (
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ padding: 48 }}
        >
          <Typography.Text type="danger">
            {t('photo.cameraError')}
          </Typography.Text>
        </Flex>
      ) : captured ? (
        /* 撮影後プレビュー */
        <Flex vertical align="center" style={{ padding: 0, width: '100%' }}>
          <img
            src={captured}
            alt="preview"
            style={{
              width: '100%',
              maxHeight: '75vh',
              objectFit: 'contain',
              background: '#000',
              display: 'block',
            }}
          />
          <Flex gap={12} style={{ padding: 16, width: '100%', justifyContent: 'center' }}>
            <Button size="large" onClick={handleRetake}>
              {t('photo.retake')}
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleUse}
              disabled={disabled}
            >
              {t('photo.usePhoto')}
            </Button>
          </Flex>
        </Flex>
      ) : (
        /* カメラプレビュー */
        <Flex vertical align="center" style={{ padding: 0, width: '100%' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              maxHeight: '75vh',
              objectFit: 'contain',
              background: '#000',
              display: 'block',
            }}
          />
          <div style={{ padding: 16, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Button
              type="primary"
              size="large"
              shape="circle"
              icon={<CameraOutlined style={{ fontSize: 24 }} />}
              onClick={handleShutter}
              style={{ width: 64, height: 64 }}
            />
          </div>
        </Flex>
      )}
    </Modal>
  );
}
